
import React from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, FileSpreadsheet, Briefcase } from "lucide-react";

interface IntroScreenProps {
  onGetStarted: () => void;
}

const IntroScreen: React.FC<IntroScreenProps> = ({ onGetStarted }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-blue-900">
      <div className="max-w-4xl w-full mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="md:w-1/2 p-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/lovable-uploads/f03afb12-24c3-47a8-be74-c3fbc24e68e4.png" 
                alt="Cardinality Logo" 
                className="h-16" 
              />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-blue-600 dark:text-blue-400 text-center">
              Cardy SP and Effort Estimator
            </h1>
            
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 dark:from-blue-800 dark:to-blue-900 p-8 text-white rounded-lg mt-4">
              <h2 className="text-2xl font-bold mb-4">Key Benefits</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Consistent estimation across teams</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Save estimation details for future reference</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Track estimation history and versions</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Export detailed estimation reports</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Compare and analyze different estimations</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">✓</div>
                  <p>Built-in guidelines and best practices</p>
                </li>
              </ul>
            </div>
          </div>

          <div className="md:w-1/2 p-8">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
                How it works
              </h3>
              <ol className="space-y-4 text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">1</div>
                  <p>Choose between a quick single estimation or a complete project estimation</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">2</div>
                  <p>Select component types complexity to get standardized story points</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">3</div>
                  <p>Review points, development hours and total effort estimates</p>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full h-6 w-6 flex items-center justify-center text-sm mr-2 mt-0.5 flex-shrink-0">4</div>
                  <p>Save projects, track versions and download estimation reports</p>
                </li>
              </ol>
            </div>

            <div className="space-y-4">
              <Button 
                onClick={onGetStarted} 
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Quick Estimation/Guideline
              </Button>
              <Button 
                onClick={() => navigate('/projects')} 
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <FileSpreadsheet className="mr-2 h-5 w-5" />
                Project Estimations
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => navigate('/presales')} 
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <Briefcase className="mr-2 h-5 w-5" />
                Presales Estimations
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
              © {new Date().getFullYear()} <a href="https://cardyai.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">cardyai.com</a>. All rights reserved. | v1.2.0
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroScreen;
